import { Outlet, useNavigate, useParams } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { SectionHeader } from '@factorialco/f0-react/dist/experimental'
import { Delete, Upload } from '@factorialco/f0-react/icons/app'
import { MyTrainingDetailCertificatesDocument } from 'generated/resources/graphql'
import { DeleteCertificateDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { MaterialItem, MaterialsList } from 'modules/trainings/components/MaterialsList'

export default function MyTrainingsCertificatesPage() {
  const { trainingId } = useParams<{ trainingId: string }>()
  const { access, employee } = useCurrentSession()
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const [deleteCertificate] = useMutation(DeleteCertificateDocument, {
    extractorFn: (data) => data.trainingsMutations.deleteTrainingsTrainingMembershipFile,
    awaitRefetchQueries: true,
    refetchQueries: [MyTrainingDetailCertificatesDocument],
  })
  const source = useGraphqlDataCollectionSource({
    document: MyTrainingDetailCertificatesDocument,
    mapData: (data) =>
      data.trainings.trainingMembershipsConnection.nodes.flatMap((membership) =>
        membership.trainingMembershipFilesConnection.nodes.map(
          (file): MaterialItem => ({
            contentType: file.contentType,
            filename: file.filename,
            id: file.id,
            size: file.size,
            title: file.filename,
            type: 'membership',
          })
        )
      ),
    mapVariables: () => ({
      trainingId: Number(trainingId),
      accessId: access.id,
      employeeId: employee.id,
    }),
    mapPagination: (data) => {
      return {
        total:
          data.trainings.trainingMembershipsConnection.nodes
            .flatMap((membership) => membership.trainingMembershipFilesConnection.totalCount)
            .at(0) ?? 0,
      }
    },
    itemUrl: (item) => {
      return navigation.myTrainings.revamp.detailsRevamp.view.certificates.view.fullPath({
        trainingId: Number(trainingId),
        membershipFileId: item.id,
      })
    },
    primaryActions: () => ({
      onClick: () => {
        navigate(
          navigation.myTrainings.revamp.detailsRevamp.view.certificates.upload.fullPath({
            trainingId: Number(trainingId),
          })
        )
      },
      label: i18n.t('common.upload'),
      icon: Upload,
    }),
    itemActions: (item) => [
      {
        onClick: async () => {
          const result = await deleteCertificate({
            variables: {
              id: item.id,
            },
          })
          handleResult(result, () => {
            app.setFlash({
              type: 'wrong',
              message: i18n.t(
                'trainings.my_trainings_revamp.training_detail.certificates.datacollection.delete_error'
              ),
            })
          })
        },
        label: i18n.t('common.delete'),
        icon: Delete,
      },
    ],
  })

  return (
    <Box gap="s16">
      <Outlet />
      <SectionHeader
        title={i18n.t(
          'trainings.my_trainings_revamp.training_detail.certificates.section_header.title'
        )}
        description={i18n.t(
          'trainings.my_trainings_revamp.training_detail.certificates.section_header.description'
        )}
        separator="bottom"
      />
      <MaterialsList
        source={source}
        emptyStates={{
          'no-data': {
            title: i18n.t(
              'trainings.my_trainings_revamp.training_detail.certificates.datacollection.empty_state.no_data.title'
            ),
            description: i18n.t(
              'trainings.my_trainings_revamp.training_detail.certificates.datacollection.empty_state.no_data.description'
            ),
          },
        }}
      />
    </Box>
  )
}
